import { IonIcon, useIonRouter, useIonViewWillEnter } from "@ionic/react";
import { arrowForward, calendarOutline, chevronForward, personAddOutline } from "ionicons/icons";
import { FC, KeyboardEvent, ReactElement, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import styled, { css } from "styled-components";
import Card from "../../components/atoms/Card";
import Info from "../../components/atoms/ErrorComponent";
import Icon from "../../components/atoms/Icon";
import {
  DeadlineChip,
  DoneMark,
  INK,
  MainPill,
  NameChip,
  TonalPill,
  inkWash,
  kitCard,
} from "../../components/atoms/Kit";
import ResonanceLoader from "../../components/atoms/ResonanceLoader/ResonanceLoader";
import { useConfig } from "../../contexts/ConfigContext";
import { ComponentType } from "../../types/Questionnaire";
import { RoleEnum } from "../../types/invitation.types";
import { getStudyTabIds } from "../../navigation/studyTabs";
import { getUpcomingWindows } from "../../services/calendar.service";
import { PAGE_BACKGROUND } from "../../theme/pageStyle";
import { useResonanceLayout } from "../../components/organisms/ResonanceContext/ResonanceLayoutContext";
import ResonanceLayout from "../../components/organisms/ResonanceContext/ResonanceLayout";
import { useResonanceData } from "../../components/organisms/ResonanceContext/ResonanceDataContext";

const SequenceComponents: FC = (): ReactElement => {
  const { study_slug } = useParams<{ study_slug: string }>();

  const {
    t,
    i18n: { language },
  } = useTranslation();
  const ionRouter = useIonRouter();
  const { setResonanceLayout } = useResonanceLayout();

  useIonViewWillEnter(() => {
    setResonanceLayout({
      disableHeader: false,
      disableNav: false,
      backgroundColor: PAGE_BACKGROUND,
      title: "Questionnaires",
      welcomePage: false,
    });
  });

  const { sequences, loading, error } = useResonanceData();
  const { selectedRole, study } = useConfig();
  // A subject viewing their own family's occurrences: the numéro de dossier is their own —
  // redundant chrome. Staff (investigator/intervenor) keep it to tell families apart.
  const isSubjectViewer = selectedRole?.name === RoleEnum.SUBJECT;

  const onComponentClick = (sequence_id: number, component_id: number) => {
    ionRouter.push(
      `/${study_slug}/tabs/questionnaires/sequences/${sequence_id}/components/${component_id}`,
      "forward",
      "push"
    );
  };

  const questionnaires = sequences
    .filter((seq) => seq.status !== "DONE") // ignore finished sequences
    .flatMap((seq) =>
      seq.components
        .filter((c) => c.questionnaire_id) // keep only questionnaire components
        .map((c) => ({
          ...c,
          sequence_id: seq.id, // keep link to parent sequence
          sequenceLabel: seq.label,
          sequenceStatus: seq.status,
          // Subject context for about-someone-else occurrences (investigator fiches,
          // a parent's per-child questionnaires) so cards about different subjects are
          // distinguishable. subjectDisplayLabel is the API's best label («Emma» /
          // «Enfant 2»); older payloads fall back to the plain role label. The backend
          // nulls these fields when the user answers about themselves, so their
          // presence is the "is about a subject" signal — and a study where everyone
          // answers about themselves never shows a name. Case id (= external_id /
          // numéro de dossier, shared by a family) stays staff-only.
          subjectRoleLabel: seq.subjectDisplayLabel ?? seq.subjectRoleLabel ?? null,
          subjectCaseId:
            !isSubjectViewer &&
            (seq.subjectDisplayLabel || seq.subjectInfo || seq.subjectRoleLabel)
              ? seq.subject?.external_id ?? null
              : null,
        }))
    )
    .filter((q) => !q.is_done) // keep only unfinished questionnaires
    .sort(
      (a, b) => new Date(a.deadline).getTime() - new Date(b.deadline).getTime()
    );

  const isInitialLoading = loading && questionnaires == null; // null or undefined
  const isEmpty = !loading && (questionnaires?.length ?? 0) === 0;

  const studyTabIds = getStudyTabIds(study_slug, selectedRole?.name);
  // The invitation tab is subject-role-gated and per-study opt-in; only nudge toward it
  // when this study/role actually has it (Outborn subjects). The invitation page itself
  // still fail-softs when no config is sendable.
  const invitationAvailable = studyTabIds.includes("invitation");
  // "Prochain : …" on the empty screen points at the Calendrier, so only where it exists.
  const calendarAvailable = studyTabIds.includes("calendar");

  // When nothing is left to fill: the next questionnaire window that opens (the same
  // upcoming windows the Calendrier lists). Fail-soft: no date line on any error.
  const [nextOpening, setNextOpening] = useState<string | null>(null);
  useEffect(() => {
    if (!isEmpty || !calendarAvailable || !study?.id) {
      setNextOpening(null);
      return;
    }
    let cancelled = false;
    getUpcomingWindows({ studyId: study.id, lang: language })
      .then(({ data }) => {
        if (cancelled) return;
        const now = Date.now();
        const next =
          (data?.upcoming ?? [])
            .map((window) => window.opens_at)
            .filter((opensAt) => new Date(opensAt).getTime() > now)
            .sort((a, b) => new Date(a).getTime() - new Date(b).getTime())[0] ?? null;
        setNextOpening(next);
      })
      .catch(() => {
        if (!cancelled) setNextOpening(null);
      });
    return () => {
      cancelled = true;
    };
  }, [isEmpty, calendarAvailable, study?.id, language]);

  const formatLongDate = (iso: string): string => {
    try {
      return new Intl.DateTimeFormat(language, {
        weekday: "long",
        day: "numeric",
        month: "long",
      }).format(new Date(iso));
    } catch {
      return new Date(iso).toLocaleDateString();
    }
  };

  if (error) {
    return (
      <Info
        title={t("common_server_error_title")}
        description={t("common_server_error_desc")}
        actionText={t("common_go_back")}
      />
    );
  }

  return (
    <ResonanceLayout isConnected>
      <>
      {isInitialLoading && <ResonanceLoader />}

      {!isInitialLoading && isEmpty && (
        <EmptyState>
          {/* Just a checkmark — the same in every study. */}
          <DoneMark />
          <EmptyTitle>{t("sequence_empty_title")}</EmptyTitle>
          <EmptyText>{t("sequence_empty_text")}</EmptyText>

          {nextOpening && (
            <NextPill
              type="button"
              onClick={() =>
                ionRouter.push(`/${study_slug}/tabs/calendar`, "forward", "push")
              }
            >
              <NextPillIcon aria-hidden="true">
                <IonIcon icon={calendarOutline} />
              </NextPillIcon>
              <span>
                {t("sequence_next_label")} <b>{formatLongDate(nextOpening)}</b>
              </span>
              <IonIcon icon={chevronForward} className="next-pill-chevron" aria-hidden="true" />
            </NextPill>
          )}

          {invitationAvailable && (
            <InviteCta
              type="button"
              onClick={() =>
                ionRouter.push(
                  `/${study_slug}/tabs/invitation`,
                  "forward",
                  "push"
                )
              }
            >
              <InviteCtaBadge aria-hidden="true">
                <IonIcon icon={personAddOutline} />
              </InviteCtaBadge>
              <InviteCtaText>
                <InviteCtaTitle>{t("invite_cta_title")}</InviteCtaTitle>
                <InviteCtaSubtitle>{t("invite_cta_subtitle")}</InviteCtaSubtitle>
              </InviteCtaText>
              <IonIcon icon={chevronForward} className="invite-cta-chevron" aria-hidden="true" />
            </InviteCta>
          )}
        </EmptyState>
      )}

      {!isInitialLoading && !isEmpty && (
        <CardList>
          <ListSummary>
            {t("sequence_list_summary", { count: questionnaires.length })}
          </ListSummary>
          {(questionnaires ?? []).map((q) => {
            const Renderer = q.questionnaire_id
              ? QuestionnaireGamified
              : ContentGamified;
            return (
              <Renderer
                key={q.id}
                questionnaire={q}
                onComponentClick={onComponentClick}
              />
            );
          })}
          <Blank />
        </CardList>
      )}
      </>
    </ResonanceLayout>
  );
};

interface PropsGamified {
  questionnaire: ComponentType & {
    subjectRoleLabel?: string | null;
    subjectCaseId?: string | null;
  };
  onComponentClick(s: number, c: number): void;
}

/**
 * One card per questionnaire (layout C): a band with whom it is about (only when
 * it is about someone else) and the deadline in words, then the title, its length,
 * progress once started, and one action. The whole card opens the questionnaire.
 */
const QuestionnaireGamified: FC<PropsGamified> = ({
  questionnaire,
  onComponentClick,
}) => {
  const { study } = useConfig();
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const open = (): void =>
    onComponentClick(questionnaire.sequence_id, questionnaire.id);
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      open();
    }
  };

  const total = questionnaire.questions_count;
  const answered = questionnaire.answered_questions_count;
  const started = answered > 0;
  // questions_count can be 0 (e.g. an information-only questionnaire — answerless
  // pages are excluded from the denominator): 0/0 is NaN.
  const percent = total > 0 ? Math.min(100, (answered / total) * 100) : 0;

  return (
    <QuestionnaireCard
      // The card's own content (title, deadline, progress, action) is its name.
      role="button"
      tabIndex={0}
      onClick={open}
      onKeyDown={onKeyDown}
    >
      <CardBand>
        {questionnaire.subjectRoleLabel && (
          <NameChip label={questionnaire.subjectRoleLabel} />
        )}
        {questionnaire.subjectCaseId && (
          <SubjectCaseId>{questionnaire.subjectCaseId}</SubjectCaseId>
        )}
        <BandDeadline>
          <DeadlineChip deadline={questionnaire.deadline} t={t} language={language} />
        </BandDeadline>
      </CardBand>

      <CardBody>
        <QuestionnaireTitle>{questionnaire.questionnaire_title}</QuestionnaireTitle>
        <QuestionnaireMeta>
          {total} {t("questionnaire_questionnaire")}
          <MetaSep aria-hidden="true">·</MetaSep>
          {questionnaire.avg_amount_time_to_answer} {t("questionnaire_minutes")}
          {/* Points only where the study is gamified. */}
          {study.is_gamified && (
            <>
              <MetaSep aria-hidden="true">·</MetaSep>
              {questionnaire.points} {t("questionnaire_point")}
            </>
          )}
        </QuestionnaireMeta>

        {started && (
          <CardProgress>
            <ProgressBarWrapper>
              <ProgressFill $width={percent} />
            </ProgressBarWrapper>
            <ProgressCount>
              {t("questionnaire_card_progress", { answered, total })}
            </ProgressCount>
          </CardProgress>
        )}

        <CardActions>
          {started ? (
            <MainPill>
              {t("questionnaire_resume")}
              <IonIcon icon={arrowForward} aria-hidden="true" />
            </MainPill>
          ) : (
            <TonalPill>
              {t("questionnaire_start")}
              <IonIcon icon={arrowForward} aria-hidden="true" />
            </TonalPill>
          )}
        </CardActions>
      </CardBody>
    </QuestionnaireCard>
  );
};

const ContentGamified: FC<PropsGamified> = ({
  questionnaire: questionnaire,
  onComponentClick,
}) => {
  return (
    <ComponentCard
      as="div"
      onClick={() => {
        onComponentClick(
          questionnaire.content_id,
          questionnaire.questionnaire_id
        );
      }}
    >
      <div>
        <ComponentTitle>{questionnaire.content_label}</ComponentTitle>
      </div>

      <ComponentIcon icon={chevronForward} />
    </ComponentCard>
  );
};

export default SequenceComponents;

/**
 * Desktop reading column.
 *
 * The page renders as direct children of IonContent, so it inherits its 1200px cap
 * and would stretch a one-line questionnaire title across a laptop screen.
 *
 * Centred with AUTO HORIZONTAL MARGINS, because this is a BLOCK formatting
 * context. IonContent's `display: flex` sits on the host element, where it
 * governs the shadow-DOM children; slotted light-DOM children are laid out inside
 * `.inner-scroll`, which Ionic gives `position: absolute` and no `display` — i.e.
 * block. So `align-self` does nothing here, and an explicit side margin pins the
 * column to the left instead of centring it.
 */
const desktopColumn = css`
  @media (min-width: 768px) {
    width: 100%;
    max-width: 620px;
    /* max-width resolves before margins, so 620px leaves free space for the
       autos to split evenly. */
    margin-left: auto;
    margin-right: auto;
  }
`;

const EmptyState = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 28px 20px 110px;
  text-align: center;
  ${desktopColumn}
`;

const EmptyTitle = styled.h2`
  margin: 14px 0 0;
  font-size: 26px;
  line-height: 1.25;
  font-weight: var(--app-font-heading-weight, 700);
  letter-spacing: -0.015em;
  color: var(--app-color-text, #0f172a);
`;

const EmptyText = styled.p`
  margin: 8px 0 0;
  max-width: 300px;
  font-size: 16px;
  line-height: 1.45;
  color: rgba(var(--app-color-text-rgb, 15, 23, 42), 0.7);
`;

const NextPill = styled.button`
  margin-top: 22px;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  min-height: 50px;
  padding: 0 14px 0 8px;
  border: none;
  border-radius: var(--app-radius-pill, 999px);
  background: var(--app-color-surface, #ffffff);
  box-shadow:
    inset 0 0 0 1px var(--app-card-line, transparent),
    var(--app-shadow-soft, 0 1px 2px rgba(15, 23, 42, 0.05));
  font-family: inherit;
  font-size: 15px;
  color: rgba(var(--app-color-text-rgb, 15, 23, 42), 0.75);
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;

  b {
    font-weight: 700;
    color: var(--app-color-text, #0f172a);
  }

  .next-pill-chevron {
    font-size: 17px;
    color: rgba(var(--app-color-text-rgb, 15, 23, 42), 0.4);
  }

  &:active {
    transform: scale(0.98);
  }
`;

const NextPillIcon = styled.span`
  flex: none;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${inkWash(0.12)};
  color: ${INK};

  ion-icon {
    font-size: 18px;
  }
`;

const InviteCta = styled.button`
  margin-top: 30px;
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
  max-width: 440px;
  padding: 16px 16px 16px 14px;
  border: none;
  cursor: pointer;
  text-align: left;
  font-family: inherit;
  -webkit-tap-highlight-color: transparent;
  ${kitCard}
  transition: transform 0.05s ease;
  &:active {
    transform: scale(0.99);
  }
  .invite-cta-chevron {
    font-size: 20px;
    color: rgba(var(--app-color-text-rgb, 15, 23, 42), 0.4);
    flex-shrink: 0;
  }
`;

const InviteCtaBadge = styled.div`
  flex-shrink: 0;
  width: 46px;
  height: 46px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${inkWash(0.12)};
  color: ${INK};
  ion-icon {
    font-size: 23px;
  }
`;

const InviteCtaText = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

const InviteCtaTitle = styled.div`
  font-family: var(--app-font-heading, inherit);
  font-size: 16.5px;
  font-weight: var(--app-font-heading-weight, 700);
  color: var(--app-color-text, #0f172a);
`;

const InviteCtaSubtitle = styled.div`
  font-size: 14px;
  line-height: 1.4;
  color: rgba(var(--app-color-text-rgb, 15, 23, 42), 0.65);
`;

const CardList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 14px 16px 0;
  ${desktopColumn}
`;

const ListSummary = styled.p`
  margin: 0 4px;
  font-size: 15px;
  font-weight: 600;
  color: rgba(var(--app-color-text-rgb, 15, 23, 42), 0.72);
`;

const QuestionnaireCard = styled.div`
  ${kitCard}
  overflow: hidden;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  transition: transform 0.12s ease;

  &:active {
    transform: scale(0.985);
  }

  &:focus-visible {
    outline: none;
    box-shadow:
      0 0 0 3px ${inkWash(0.35)},
      var(--app-shadow-card, 0 1px 2px rgba(15, 23, 42, 0.04));
  }
`;

/* Top band: whom (only when it's about someone else), case id (staff), deadline. */
const CardBand = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px 10px;
  min-height: 46px;
  padding: 9px 16px;
  box-sizing: border-box;
  background: ${inkWash(0.06)};
`;

/* The deadline always sits on the right, with or without a name chip. */
const BandDeadline = styled.span`
  margin-left: auto;
  display: inline-flex;
`;

const SubjectCaseId = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: rgba(var(--app-color-text-rgb, 15, 23, 42), 0.6);
`;

const CardBody = styled.div`
  padding: 14px 18px 16px;
`;

const QuestionnaireTitle = styled.h2`
  margin: 0;
  font-family: var(--app-font-heading, inherit);
  font-size: 20px;
  line-height: 1.25;
  font-weight: var(--app-font-heading-weight, 700);
  letter-spacing: -0.01em;
  color: var(--app-color-text, #0f172a);

  /* Same step as the desktop heading scale (App.scss). */
  @media (min-width: 768px) {
    font-size: 18px;
  }
`;

const QuestionnaireMeta = styled.p`
  margin: 6px 0 0;
  font-size: 14.5px;
  line-height: 1.35;
  color: rgba(var(--app-color-text-rgb, 15, 23, 42), 0.7);
`;

const MetaSep = styled.span`
  margin: 0 7px;
  color: rgba(var(--app-color-text-rgb, 15, 23, 42), 0.35);
`;

const CardProgress = styled.div`
  margin-top: 14px;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ProgressBarWrapper = styled.div`
  flex: 1 1 auto;
  background-color: rgba(var(--app-color-text-rgb, 4, 23, 35), 0.08);
  border-radius: 6px;
  height: 8px;
  overflow: hidden;
`;

const ProgressFill = styled.div<{ $width?: number }>`
  /* Progress fills sweep from the emphasis tone (strong, derived from
     primary) at the leading edge to its pale -soft end — the visible
     gradient the historical bar had. */
  background: linear-gradient(
    270deg,
    var(--app-color-emphasis, var(--app-color-primary, #2db6b4)) 0%,
    var(--app-color-emphasis-soft, var(--app-color-primary-tint, #abf0ef)) 105%
  );
  height: 100%;
  border-radius: 6px;
  width: ${(props) => props.$width}%;
  transition: width 0.4s ease-in-out;
`;

const ProgressCount = styled.span`
  flex: none;
  font-size: 13px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: rgba(var(--app-color-text-rgb, 15, 23, 42), 0.7);
`;

const CardActions = styled.div`
  margin-top: 16px;
  display: flex;
  justify-content: flex-end;
`;

const Blank = styled.div`
  width: 100%;
  height: 70px;
`;

const ComponentCard = styled(Card)`
  padding: 20px 10px;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const ComponentTitle = styled.div`
  font-size: 18px;
  font-weight: bold;
`;

const ComponentIcon = styled(Icon)`
  color: #929292;
  font-size: 20px;
`;
